import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'

export const DOMAINS = [
  ['domain_memory', 'Memory'],
  ['domain_mobility', 'Mobility'],
  ['domain_mood', 'Mood'],
  ['domain_sleep', 'Sleep'],
  ['domain_appetite', 'Appetite'],
  ['domain_behaviour', 'Behaviour'],
  ['domain_pain', 'Pain (comfort)'],
  ['domain_communication', 'Communication'],
  ['domain_personal_care', 'Personal care'],
  ['domain_social_engagement', 'Social engagement'],
]
export const TRAJECTORIES = [
  ['stable', 'Stable'],
  ['improvement', 'Improvement'],
  ['gradual_decline', 'Gradual decline'],
  ['acute_change', 'Acute change'],
]

const thisMonth = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default function ProgressForm({ recipientId, existing, onDone, onCancel }) {
  const { user } = useAuth()
  const toast = useToast()
  const [month, setMonth] = useState(existing ? existing.month.slice(0, 7) : thisMonth())
  const [scores, setScores] = useState(() => {
    const init = {}
    for (const [k] of DOMAINS) init[k] = existing?.[k] ?? null
    return init
  })
  const [trajectory, setTrajectory] = useState(existing?.trajectory || 'stable')
  const [notes, setNotes] = useState(existing?.notes || '')
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    try {
      const payload = {
        care_recipient_id: recipientId,
        logged_by: user.id,
        month: `${month}-01`,
        ...scores,
        trajectory,
        notes: notes.trim() || null,
      }
      const { error } = await supabase
        .from('monthly_progress')
        .upsert(payload, { onConflict: 'care_recipient_id,month' })
      if (error) throw error
      toast.success('Progress saved.')
      onDone()
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div onClick={() => !busy && onCancel()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>Monthly progress</h2>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>
        <div className="stack">
          <div className="field"><label className="label">Month</label><input type="month" className="input" value={month} onChange={(e) => setMonth(e.target.value)} /></div>

          <div className="section-title">Rate each domain (1 low – 5 high)</div>
          {DOMAINS.map(([key, label]) => (
            <div key={key} className="field" style={{ gap: 4 }}>
              <label className="label">{label}</label>
              <div className="row" style={{ gap: 6 }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const on = scores[key] === n
                  return (
                    <button key={n} type="button" onClick={() => setScores((s) => ({ ...s, [key]: on ? null : n }))}
                      style={{ flex: 1, height: 42, borderRadius: 9, fontWeight: 700, border: on ? 'none' : '1.5px solid var(--border)', background: on ? 'var(--teal)' : 'var(--surface)', color: on ? '#fff' : 'var(--text)' }}>{n}</button>
                  )
                })}
              </div>
            </div>
          ))}

          <div className="field">
            <label className="label">Overall trajectory</label>
            <div className="row wrap" style={{ gap: 8 }}>
              {TRAJECTORIES.map(([v, l]) => (
                <button key={v} type="button" className={`chip ${trajectory === v ? 'chip-on' : ''}`} onClick={() => setTrajectory(v)}>{l}</button>
              ))}
            </div>
          </div>

          <div className="field"><label className="label">Notes</label><textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional context for this month" /></div>

          <button className="btn btn-lg btn-block" onClick={save} disabled={busy}>{busy ? <span className="spinner" /> : 'Save progress'}</button>
        </div>
      </div>
    </div>
  )
}
